import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import useAsyncData from '../hooks/useAsyncData';
import useStaleWarning from '../hooks/useStaleWarning';
import useAutoRefresh from '../hooks/useAutoRefresh';

const AlertTicker = () => {
  const { data: alerts, loading, error, retry } = useAsyncData('/api/alerts');
  const { isStale, lastUpdated } = useStaleWarning(alerts);
  useAutoRefresh(retry);

  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–';

  if (loading) {
    return (
      <ErrorBoundary>
        <article className="panel alert-panel" aria-busy="true">
          <h2>Event Ticker</h2>
          <div className="skeleton">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </article>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary>
        <article className="panel alert-panel" aria-live="assertive">
          <h2>Event Ticker</h2>
          <p className="error-msg">Failed to load alerts.</p>
          <button type="button" className="btn-retry" onClick={retry}>Retry</button>
        </article>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <article className="panel alert-panel" aria-label="System alerts" aria-busy="false">
        <div className="panel-header">
          <h2>Event Ticker</h2>
          <div className="panel-meta">
            <span className="last-updated" aria-label="Last updated">
              Last updated: {fmtTime(lastUpdated)}
            </span>
            {isStale && (
              <span className="badge badge-stale" role="status">Stale</span>
            )}
            <button type="button" className="btn-icon btn-refresh" onClick={retry} aria-label="Refresh alerts">
              ↻
            </button>
          </div>
        </div>
        <div className="alert-list" role="log" aria-live="polite" aria-label="Latest system alerts">
          {alerts && alerts.length === 0 && <span className="clear">No active alerts</span>}
          {alerts && alerts.map((a, i) => (
            <div key={i} className={`alert alert-${a.level}`}>
              <span className="alert-level">[{a.level.toUpperCase()}]</span>
              <span>{a.text}</span>
            </div>
          ))}
        </div>
      </article>
    </ErrorBoundary>
  );
};

export default AlertTicker;
